/**
 * The access token carries `perms`, so the UI can decide what to render without
 * a second round trip. Decoding is not verification — the signature is checked
 * by the backend on every call, and this claim is only used to hide controls.
 */
interface AccessTokenClaims {
  sub?: string;
  perms?: string[] | string;
  role?: string;
  org_id?: string;
  /** True when the role carries `is_platform` — the backend's `AppUser.isPlatformUser()`. */
  plat?: boolean;
  exp?: number;
}

export function decodeJwt(token: string): AccessTokenClaims | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json))) as AccessTokenClaims;
  } catch {
    try {
      return JSON.parse(atob(payload)) as AccessTokenClaims;
    } catch {
      return null;
    }
  }
}

export function jwtPermissions(token: string): string[] {
  const claims = decodeJwt(token);
  if (!claims?.perms) return [];
  return Array.isArray(claims.perms) ? claims.perms : String(claims.perms).split(/[\s,]+/).filter(Boolean);
}

export function jwtOrganizationId(token: string): string | null {
  return decodeJwt(token)?.org_id ?? null;
}

/**
 * Platform staff is a property of the role, not of the organization: a founder
 * mid-registration also has no `org_id`, so a missing organization must never
 * be read as "platform". Only the explicit `plat` claim counts.
 */
export function jwtIsPlatform(token: string): boolean {
  return decodeJwt(token)?.plat === true;
}
